import type { FC, ReactNode } from 'react'
import type { ResourceSummary } from '@/types'

import Link from 'next/link'
import { ArrowUpRight, Clock } from 'lucide-react'
import { cn } from '@/lib/utils'
import { formatDate } from '@/lib/format'
import { LogoMark } from '@/components/generals/logo'

interface ResourceCoverProps {
    resource: ResourceSummary
    className?: string
    eager?: boolean
}

export const ResourceCover: FC<ResourceCoverProps> = ({ resource, className, eager = false }): ReactNode => {
    return (
        <div className={cn('relative overflow-hidden rounded-[22px] bg-burgundy', className)}>
            {resource.coverImage ? (
                // eslint-disable-next-line @next/next/no-img-element
                <img
                    src={resource.coverImage}
                    alt={resource.coverImageAlt ?? ''}
                    loading={eager ? 'eager' : 'lazy'}
                    decoding='async'
                    className='absolute inset-0 size-full object-cover saturate-[0.85] transition-transform duration-[1.2s] ease-out-expo group-hover:scale-[1.04]'
                />
            ) : (
                <div aria-hidden='true' className='absolute inset-0 flex items-center justify-center'>
                    <LogoMark className='size-1/3 text-gold/80' />
                </div>
            )}

            <div aria-hidden='true' className='pointer-events-none absolute inset-0 bg-gradient-to-t from-burgundy/25 via-transparent to-transparent' />
        </div>
    )
}

const Meta: FC<{ resource: ResourceSummary, tone?: 'dark' | 'light' }> = ({ resource, tone = 'dark' }): ReactNode => {
    return (
        <p className={cn('flex flex-wrap items-center gap-x-3 gap-y-1 text-xs', tone === 'dark' ? 'text-stone' : 'text-cream/60')}>
            {resource.category && (
                <span className={cn('rounded-full px-2.5 py-1 font-medium', tone === 'dark' ? 'bg-burgundy/[0.06] text-burgundy' : 'bg-cream/10 text-cream')}>
                    {resource.category.name}
                </span>
            )}

            {resource.publishedAt && <span>{formatDate(resource.publishedAt)}</span>}

            {resource.readMinutes ? (
                <span className='inline-flex items-center gap-1'>
                    <Clock aria-hidden='true' className='size-3' />
                    {resource.readMinutes} min read
                </span>
            ) : null}
        </p>
    )
}

interface ResourceCardProps {
    resource: ResourceSummary
    featured?: boolean
    className?: string
}

export const ResourceCard: FC<ResourceCardProps> = ({ resource, featured = false, className }): ReactNode => {
    if (featured) {
        return (
            <article className={cn('group', className)}>
                <Link href={`/resources/${resource.slug}`} className='grid gap-8 lg:grid-cols-12 lg:items-center lg:gap-12'>
                    <ResourceCover resource={resource} eager className='aspect-[16/10] lg:col-span-7' />

                    <div className='lg:col-span-5'>
                        <p className='font-mono text-[10.5px] font-medium uppercase tracking-[0.2em] text-gold-dark'>Featured</p>

                        <h2 className='mt-4 font-heading text-display-md font-medium text-burgundy transition-colors duration-300 group-hover:text-burgundy-muted'>
                            {resource.title}
                        </h2>

                        {resource.excerpt && <p className='mt-5 text-lg leading-relaxed text-stone-dark'>{resource.excerpt}</p>}

                        <div className='mt-6'>
                            <Meta resource={resource} />
                        </div>

                        <span className='mt-8 inline-flex items-center gap-2 border-b border-burgundy/30 pb-1 text-sm font-medium text-burgundy transition-colors duration-300 group-hover:border-burgundy'>
                            Read the article
                            <ArrowUpRight aria-hidden='true' className='size-4 transition-transform duration-300 group-hover:-translate-y-0.5 group-hover:translate-x-0.5' />
                        </span>
                    </div>
                </Link>
            </article>
        )
    }

    return (
        <article className={cn('group h-full', className)}>
            <Link href={`/resources/${resource.slug}`} className='flex h-full flex-col'>
                <ResourceCover resource={resource} className='aspect-[4/3]' />

                <div className='mt-6 flex flex-1 flex-col'>
                    <Meta resource={resource} />

                    <h3 className='mt-4 font-heading text-[1.4rem] font-medium leading-snug tracking-[-0.02em] text-burgundy transition-colors duration-300 group-hover:text-burgundy-muted'>
                        {resource.title}
                    </h3>

                    {resource.excerpt && <p className='mt-3 line-clamp-3 leading-relaxed text-stone-dark'>{resource.excerpt}</p>}

                    <span className='mt-auto inline-flex items-center gap-1.5 pt-6 text-sm font-medium text-burgundy'>
                        Read more
                        <ArrowUpRight aria-hidden='true' className='size-4 transition-transform duration-300 group-hover:-translate-y-0.5 group-hover:translate-x-0.5' />
                    </span>
                </div>
            </Link>
        </article>
    )
}
